import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import dagre from 'dagre';
import '@xyflow/react/dist/style.css';
import { GET_LINEAGE_GRAPH, FIND_AFFECTED_IMAGES } from '../graphql/queries';

const nodeWidth = 220;
const nodeHeight = 80;

function getLayoutedElements(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 50, ranksep: 80 });

  nodes.forEach((node) => g.setNode(node.id, { width: nodeWidth, height: nodeHeight }));
  edges.forEach((edge) => g.setEdge(edge.source, edge.target));

  dagre.layout(g);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = g.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

function statusColor(status) {
  const map = { completed: '#00b894', failed: '#d63031', deprecated: '#fdcb6e', pending: '#0984e3' };
  return map[status] || '#636e72';
}

export default function ImageLineage() {
  const { imageId } = useParams();
  const navigate = useNavigate();
  const [affected, setAffected] = useState(null);

  const { data, loading, error } = useQuery(GET_LINEAGE_GRAPH, {
    variables: { imageId },
  });

  const [findAffected, { loading: findingAffected }] = useLazyQuery(FIND_AFFECTED_IMAGES);

  const { initialNodes, initialEdges } = useMemo(() => {
    if (!data?.getLineageGraph) return { initialNodes: [], initialEdges: [] };

    const graph = data.getLineageGraph;
    const flowNodes = graph.nodes.map((n) => ({
      id: n.id,
      type: 'default',
      data: {
        label: (
          <div style={{ textAlign: 'center', fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>{n.name}</div>
            <div style={{ fontSize: 10, color: '#636e72' }}>{n.blueprintName}</div>
            <span style={{
              fontSize: 10,
              padding: '1px 6px',
              borderRadius: 4,
              background: statusColor(n.status) + '22',
              color: statusColor(n.status),
            }}>
              {n.status}
            </span>
          </div>
        ),
      },
      style: {
        border: n.id === imageId ? '2px solid #0984e3' : '1px solid #dfe6e9',
        borderRadius: 8,
        padding: 8,
        width: nodeWidth,
        background: n.id === imageId ? '#0984e310' : '#fff',
      },
    }));

    const flowEdges = graph.edges.map((e, i) => ({
      id: `e-${i}`,
      source: e.source,
      target: e.target,
      animated: true,
      style: { stroke: '#0984e3' },
    }));

    return getLayoutedElements(flowNodes, flowEdges);
  }, [data, imageId]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes/edges when data changes
  React.useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onNodeClick = useCallback((_, node) => {
    if (node.id !== imageId) {
      navigate(`/images/${node.id}`);
    }
  }, [navigate, imageId]);

  const handleFindAffected = async () => {
    const image = data?.getLineageGraph?.nodes?.find(n => n.id === imageId);
    if (image?.amiId) {
      const result = await findAffected({ variables: { baseAmiId: image.amiId } });
      setAffected(result.data?.findAffectedImages || []);
    }
  };

  if (loading) return <div className="loading">Loading lineage graph...</div>;
  if (error) return <div className="error">Failed to load lineage: {error.message}</div>;

  return (
    <div>
      <h2 className="page-title">Image Lineage</h2>

      <div className="card" style={{ height: 500, padding: 0 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          fitView
          attributionPosition="bottom-left"
        >
          <Controls />
          <MiniMap />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>
      </div>

      <div className="actions">
        <button
          className="btn btn-primary"
          style={{ background: '#e17055' }}
          onClick={handleFindAffected}
          disabled={findingAffected}
        >
          {findingAffected ? 'Searching...' : 'Find Affected Images'}
        </button>
      </div>

      {affected && (
        <div className="card">
          <h3>Affected Downstream Images ({affected.length})</h3>
          {affected.length === 0 ? (
            <p style={{ color: '#636e72' }}>No downstream images found.</p>
          ) : (
            <table className="table">
              <thead>
                <tr><th>Name</th><th>AMI ID</th><th>Status</th><th>Created</th></tr>
              </thead>
              <tbody>
                {affected.map((img) => (
                  <tr key={img.id}>
                    <td><a href={`/images/${img.id}`} className="link">{img.name}</a></td>
                    <td><code>{img.amiId}</code></td>
                    <td><span className={`badge badge-${img.status === 'completed' ? 'success' : 'error'}`}>{img.status}</span></td>
                    <td>{img.createdAt ? new Date(img.createdAt).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
